import i18next, { type Resource, type i18n as I18nInstance } from "i18next";
import { initReactI18next } from "react-i18next";

export const defaultLanguage = "de" as const;
export const fallbackLanguage = "en" as const;

// de first, so the picker offers the default language first
export const languages = {
  de: "German",
  en: "English",
} as const;

export type LanguageCode = keyof typeof languages;

export type LanguageStorageClient = {
  onError?: (
    error: unknown,
    context: { phase: "get" | "set"; language?: string },
  ) => void;
  getLanguage: () => Promise<string | null> | string | null;
  setLanguage: (language: string) => Promise<void> | void;
};

export type CreateI18nOptions = {
  storage?: LanguageStorageClient;
  resources: Resource;
  initialLanguage?: string;
};

export function getSupportedLanguage(language?: string | null): LanguageCode {
  if (!language) {
    return defaultLanguage;
  }

  if (language in languages) {
    return language as LanguageCode;
  }

  const baseLanguage = language.split("-")[0];
  if (baseLanguage in languages) {
    return baseLanguage as LanguageCode;
  }

  return defaultLanguage;
}

export function createI18n(options: CreateI18nOptions): I18nInstance {
  const i18n = i18next.createInstance();

  i18n.use(initReactI18next).init({
    resources: options.resources,
    lng: options.initialLanguage ?? defaultLanguage,
    fallbackLng: fallbackLanguage,
    interpolation: {
      escapeValue: false,
    },
  });

  if (options.storage) {
    void hydrateLanguage(i18n, options.storage);
  }

  return i18n;
}

export async function setLanguage(
  i18n: I18nInstance,
  language: string,
  storage?: LanguageStorageClient,
) {
  await i18n.changeLanguage(language);

  if (!storage) {
    return;
  }

  try {
    await storage.setLanguage(language);
  } catch (error) {
    storage.onError?.(error, { phase: "set", language });
  }
}

async function hydrateLanguage(
  i18n: I18nInstance,
  storage: LanguageStorageClient,
) {
  try {
    const persistedLanguage = await storage.getLanguage();
    if (!persistedLanguage) {
      return;
    }

    await i18n.changeLanguage(getSupportedLanguage(persistedLanguage));
  } catch (error) {
    storage.onError?.(error, { phase: "get" });
  }
}
